import { api } from '../../../common/api/httpClient';

export type PortalPage = {
  applicationCode: string;
  pageCode: string;
  name: string;
  applicationName: string;
  routePath: string;
};

export type PortalPageGroup = {
  applicationCode: string;
  applicationName: string;
  pages: PortalPage[];
};

export type PortalPageGroupFilter = Pick<PortalPageGroup, 'applicationCode' | 'applicationName'>;
export type PortalPageCatalog = {
  total: number;
  page: number;
  pageSize: number;
  groupFilters: PortalPageGroupFilter[];
  groups: PortalPageGroup[];
};
export type PortalPageFavoriteState = { version: number; pages: PortalPage[] };
export type PortalPageIdentity = Pick<PortalPage, 'applicationCode' | 'pageCode'>;

export type PageCatalogQuery = {
  page?: number;
  pageSize?: number;
  applicationCode?: string;
  keyword?: string;
  favoriteOnly?: boolean;
};

export const pagesApi = {
  catalog: (query: PageCatalogQuery = {}) => {
    const values = new URLSearchParams();
    if (query.page) values.set('page', String(query.page));
    if (query.pageSize) values.set('pageSize', String(query.pageSize));
    if (query.applicationCode) values.set('applicationCode', query.applicationCode);
    if (query.keyword?.trim()) values.set('keyword', query.keyword.trim());
    if (query.favoriteOnly) values.set('favoriteOnly', 'true');
    const search = values.toString();
    return api<PortalPageCatalog>(`/api/portal/pages${search ? `?${search}` : ''}`);
  },
  favorites: () => api<PortalPageFavoriteState>('/api/portal/page-favorites'),
  replaceFavorites: (version: number, pages: PortalPageIdentity[]) =>
    api<PortalPageFavoriteState>('/api/portal/page-favorites', {
      method: 'PUT', body: JSON.stringify({ version, pages }),
    }),
};

export function pageIdentity(page: PortalPageIdentity) {
  return `${page.applicationCode}:${page.pageCode}`;
}

export function mergePageGroups(current: PortalPageGroup[], next: PortalPageGroup[]) {
  const groups = new Map(current.map(group => [group.applicationCode, {
    ...group, pages: [...group.pages],
  }]));
  next.forEach(group => {
    const existing = groups.get(group.applicationCode);
    if (!existing) {
      groups.set(group.applicationCode, { ...group, pages: [...group.pages] });
      return;
    }
    const known = new Set(existing.pages.map(pageIdentity));
    group.pages.forEach(page => {
      if (!known.has(pageIdentity(page))) {
        existing.pages.push(page);
        known.add(pageIdentity(page));
      }
    });
  });
  return [...groups.values()];
}
